import React, { useEffect, useState } from "react";
import { api_Routes } from "../../tools/api_Routes";
import { Helper } from "../../tools/Helper";
import {
    LinearProgress,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper,
    Button,
    TablePagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useNavigate } from "react-router-dom";
import { FaEye, FaFileExcel } from "react-icons/fa";
import CloseIcon from "@mui/icons-material/Close";
import * as XLSX from 'xlsx';

const ResellersStatistics = () => {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [summary, setSummary] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [dateRange, setDateRange] = useState({ start: null, end: null });
    const [currentPage, setCurrentPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Reseller details dialog states
    const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
    const [selectedReseller, setSelectedReseller] = useState(null);
    const [resellerOrders, setResellerOrders] = useState([]);
    const [resellerSummary, setResellerSummary] = useState({});
    const [ordersLoading, setOrdersLoading] = useState(false);
    const [ordersCurrentPage, setOrdersCurrentPage] = useState(0);
    const [ordersRowsPerPage, setOrdersRowsPerPage] = useState(10);
    const [ordersTotal, setOrdersTotal] = useState(0);

    // Filters for reseller orders
    const [orderStatusFilter, setOrderStatusFilter] = useState('all');
    const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');
    const [exportLoading, setExportLoading] = useState(false);

    const getResellersStatistics = async () => {
        setIsLoading(true);

        const queryParams = [];
        if (dateRange.start && dateRange.end) {
            queryParams.push(`start_date=${dateRange.start.toISOString().split("T")[0]}`);
            queryParams.push(`end_date=${dateRange.end.toISOString().split("T")[0]}`);
        }

        const queryString = queryParams.length ? `?${queryParams.join("&")}` : "";

        const { response, message } = await Helper.Get({
            url: `${api_Routes.Resellers.Statistics}${queryString}`,
            hasToken: true,
        });
console.log(response)
        if (response) {
            setData(response.data.resellers);
            setSummary(response.data.summary);
        } else {
            setData([]);
            setSummary({});
        }
        setIsLoading(false);
    };

    const getResellerOrders = async (page = ordersCurrentPage + 1, perPage = ordersRowsPerPage) => {
        if (!selectedReseller) return;

        setOrdersLoading(true);

        const queryParams = [
            `reseller_id=${selectedReseller.id}`,
            `per_page=${perPage}`,
            `page=${page}`,
        ];

        if (dateRange.start && dateRange.end) {
            queryParams.push(`start_date=${dateRange.start.toISOString().split("T")[0]}`);
            queryParams.push(`end_date=${dateRange.end.toISOString().split("T")[0]}`);
        }

        if (orderStatusFilter && orderStatusFilter !== 'all') {
            queryParams.push(`order_status=${orderStatusFilter}`);
        }

        if (paymentStatusFilter && paymentStatusFilter !== 'all') {
            queryParams.push(`payment_status=${paymentStatusFilter}`);
        }

        const queryString = queryParams.length ? `?${queryParams.join("&")}` : "";

        const { response, message } = await Helper.Get({
            url: `${api_Routes.Resellers.StatisticsDetails}${queryString}`,
            hasToken: true,
        });

        if (response) {
            setResellerOrders(response.data.orders);
            setResellerSummary(response.data.summary);
            setOrdersTotal(response.pagination.total);
        } else {
            setResellerOrders([]);
            setResellerSummary({});
        }
        setOrdersLoading(false);
    };

    const handleViewDetails = (reseller) => {
        setSelectedReseller(reseller);
        setOrdersCurrentPage(0);
        setOrderStatusFilter('all');
        setPaymentStatusFilter('all');
        setDetailsDialogOpen(true);
    };

    useEffect(() => {
        getResellersStatistics();
    }, []);

    useEffect(() => {
        if (detailsDialogOpen && selectedReseller) {
            getResellerOrders();
        }
    }, [detailsDialogOpen, selectedReseller, ordersCurrentPage, ordersRowsPerPage, orderStatusFilter, paymentStatusFilter]);

    const handleDateFilterApply = () => {
        setCurrentPage(0);
        getResellersStatistics();
    };

    const handleResetFilters = () => {
        setDateRange({ start: null, end: null });
        setCurrentPage(0);
        getResellersStatistics();
    };

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setCurrentPage(0);
    };

    const handleOrdersPageChange = (event, value) => {
        setOrdersCurrentPage(value);
    };

    const handleOrdersRowsPerPageChange = (event) => {
        setOrdersRowsPerPage(parseInt(event.target.value, 10));
        setOrdersCurrentPage(0);
    };

    const handleCloseDetailsDialog = () => {
        setDetailsDialogOpen(false);
        setSelectedReseller(null);
        setResellerOrders([]);
        setResellerSummary({});
    };

    const exportToExcel = async () => {
        setExportLoading(true);
        try {
            const queryParams = [];
            if (dateRange.start && dateRange.end) {
                queryParams.push(`start_date=${dateRange.start.toISOString().split("T")[0]}`);
                queryParams.push(`end_date=${dateRange.end.toISOString().split("T")[0]}`);
            }

            const queryString = queryParams.length ? `?${queryParams.join("&")}` : "";
            const url = `${api_Routes.Resellers.Statistics}${queryString}`;

            const { response, message } = await Helper.Get({
                url,
                hasToken: true,
            });

            if (response) {
                // Create worksheet for resellers statistics
                const worksheet = XLSX.utils.json_to_sheet(
                    response.data.resellers.map(item => ({
                        'Reseller ID': item.id,
                        'Reseller Name': item.name,
                        'Orders Count': item.orders_count,
                        'Paid Orders Count': item.paid_orders_count,
                        'Total Sales': item.total_sales,
                        'Paid Sales': item.paid_sales,
                        'Pending Sales': item.pending_sales,
                        'Customers Count': item.customers_count,
                        'Panels Count': item.panels_count,
                        'Created At': item.created_at,
                    }))
                );

                // Create workbook
                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, "Resellers Statistics");

                // Add summary sheet
                const summarySheet = XLSX.utils.json_to_sheet([
                    {
                        'Total Resellers': response.data.summary.total_resellers,
                        'Total Orders': response.data.summary.total_orders,
                        'Total Paid Orders': response.data.summary.total_paid_orders,
                        'Total Sales': response.data.summary.total_sales,
                        'Total Paid Sales': response.data.summary.total_paid_sales,
                        'Total Pending Sales': response.data.summary.total_pending_sales,
                    }
                ]);
                XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");

                // Export file
                XLSX.writeFile(workbook, `Resellers_Statistics_${new Date().toISOString().split('T')[0]}.xlsx`);
            } else {
                throw new Error(message || 'Failed to export data');
            }
        } catch (error) {
            console.error('Export error:', error);
            alert('Failed to export data. Please try again.');
        } finally {
            setExportLoading(false);
        }
    };

    const exportResellerOrdersToExcel = async () => {
        if (!selectedReseller) return;

        setExportLoading(true);
        try {
            const queryParams = [`reseller_id=${selectedReseller.id}`];

            if (dateRange.start && dateRange.end) {
                queryParams.push(`start_date=${dateRange.start.toISOString().split("T")[0]}`);
                queryParams.push(`end_date=${dateRange.end.toISOString().split("T")[0]}`);
            }

            if (orderStatusFilter && orderStatusFilter !== 'all') {
                queryParams.push(`order_status=${orderStatusFilter}`);
            }

            if (paymentStatusFilter && paymentStatusFilter !== 'all') {
                queryParams.push(`payment_status=${paymentStatusFilter}`);
            }

            const queryString = queryParams.length ? `?${queryParams.join("&")}` : "";
            const url = `${api_Routes.Resellers.StatisticsDetails}${queryString}&per_page=1000`;

            const { response, message } = await Helper.Get({
                url,
                hasToken: true,
            });

            if (response) {
                // Create worksheet
                const worksheet = XLSX.utils.json_to_sheet(
                    response.data.orders.map(item => ({
                        'Order ID': item.request_id,
                        'Service': item.our_service || 'N/A',
                        'Service Type': item.our_service_type || 'N/A',
                        'User Name': item.user_name || 'N/A',
                        'Price': item.price,
                        'Discount': item.discount_type !== 'none' && (item.discount_for_auto_pay || item.discount_type === 'percentage')
                            ? (item.discount * item.price / 100) + (item.discount_for_auto_pay * item.price / 100)
                            : item.discount || 0,
                        'Net Price': item.discount_type !== 'none' && (item.discount_for_auto_pay || item.discount_type === 'percentage')
                            ? item.price - (item.discount * item.price / 100) + (item.discount_for_auto_pay * item.price / 100)
                            : item.price - item.discount || 0,
                        'Panel URL': item.panel_url || 'N/A',
                        'Created At': item.created_at,
                        'Payment Status': item.status_payment,
                        'Order Status': item.status,
                        'Payment Method': item.payment_method,
                    }))
                );

                // Create workbook
                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, `${selectedReseller.name} Orders`);

                // Add summary sheet
                const summarySheet = XLSX.utils.json_to_sheet([
                    {
                        'Reseller Name': selectedReseller.name,
                        'Total Orders': response.data.summary.total_orders,
                        'Total Sales': response.data.summary.total_sales,
                    }
                ]);
                XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");

                // Export file
                XLSX.writeFile(workbook, `${selectedReseller.name}_Orders_${new Date().toISOString().split('T')[0]}.xlsx`);
            } else {
                throw new Error(message || 'Failed to export data');
            }
        } catch (error) {
            console.error('Export error:', error);
            alert('Failed to export data. Please try again.');
        } finally {
            setExportLoading(false);
        }
    };

    // Paginate resellers data
    const paginatedResellers = data?.slice(currentPage * rowsPerPage, (currentPage + 1) * rowsPerPage);

    return (
        <div>
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-800">Resellers Statistics</h1>
                <p className="text-gray-600">View orders and sales statistics for all resellers</p>
            </div>

            {/* Summary Cards */}
            <Grid container spacing={3} className="mb-6">
                <Grid item xs={12} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Total Resellers
                            </Typography>
                            <Typography variant="h4">
                                {summary.total_resellers || 0}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Total Orders
                            </Typography>
                            <Typography variant="h4">
                                {summary.total_orders || 0}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Paid: {summary.total_paid_orders || 0}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Total Sales
                            </Typography>
                            <Typography variant="h4">
                                ${summary?.total_sales?.toFixed(2) || 0}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Pending: ${summary?.total_pending_sales?.toFixed(2) || 0}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Paid Sales
                            </Typography>
                            <Typography variant="h4" style={{ color: '#28a745' }}>
                                ${summary?.total_paid_sales?.toFixed(2) || 0}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                {summary.total_paid_sales > 0
                                    ? `${Math.round((summary.total_paid_sales / summary.total_sales) * 100)}% of total`
                                    : 'No paid sales'
                                }
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Filters Section */}
            <div className="bg-white p-4 rounded-lg shadow mb-6">
                <div className="flex flex-col lg:flex-row gap-4 items-end">
                    <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="Start Date"
                                    value={dateRange.start}
                                    onChange={(newValue) => {
                                        setDateRange(prev => ({ ...prev, start: newValue }));
                                    }}
                                    renderInput={(params) => <TextField {...params} fullWidth />}
                                />
                            </LocalizationProvider>
                        </div>
                        <div>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="End Date"
                                    value={dateRange.end}
                                    onChange={(newValue) => {
                                        setDateRange(prev => ({ ...prev, end: newValue }));
                                    }}
                                    renderInput={(params) => <TextField {...params} fullWidth />}
                                />
                            </LocalizationProvider>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleDateFilterApply}
                            disabled={!dateRange.start || !dateRange.end}
                        >
                            Apply Filter
                        </Button>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={handleResetFilters}
                            disabled={!dateRange.start && !dateRange.end}
                        >
                            Reset
                        </Button>
                        <Button
                            variant="contained"
                            color="success"
                            onClick={exportToExcel}
                            disabled={exportLoading || data.length === 0}
                            startIcon={<FaFileExcel />}
                        >
                            {exportLoading ? 'Exporting...' : 'Export'}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Resellers Table */}
            {isLoading ? (
                <LinearProgress />
            ) : data.length > 0 ? (
                <>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow className="bg-primary">
                                    <TableCell align="center" className="!text-white">Reseller ID</TableCell>
                                    <TableCell align="center" className="!text-white">Reseller Name</TableCell>
                                    <TableCell align="center" className="!text-white">Orders Count</TableCell>
                                    <TableCell align="center" className="!text-white">Total Sales</TableCell>
                                    <TableCell align="center" className="!text-white">Paid Sales</TableCell>
                                    <TableCell align="center" className="!text-white">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedResellers.map((reseller) => (
                                    <TableRow key={reseller.id}>
                                        <TableCell align="center">{reseller.id}</TableCell>
                                        <TableCell align="center">{reseller.name}</TableCell>
                                        <TableCell align="center">
                                            <div>
                                                {reseller.orders_count}
                                                {reseller.paid_orders_count > 0 && (
                                                    <Typography variant="caption" display="block" color="textSecondary">
                                                        ({reseller.paid_orders_count} paid)
                                                    </Typography>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell align="center">
                                            <div>
                                                ${reseller.total_sales.toFixed(2)}
                                                {reseller.pending_sales > 0 && (
                                                    <Typography variant="caption" display="block" color="warning.main">
                                                        (${reseller.pending_sales.toFixed(2)} pending)
                                                    </Typography>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography style={{ color: '#28a745', fontWeight: 'bold' }}>
                                                ${reseller.paid_sales.toFixed(2)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<FaEye />}
                                                onClick={() => handleViewDetails(reseller)}
                                            >
                                                View Orders
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <div className="mt-4 flex justify-center">
                        <TablePagination
                            component="div"
                            count={data.length}
                            page={currentPage}
                            onPageChange={handlePageChange}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            rowsPerPageOptions={[5, 10, 25, 50]}
                        />
                    </div>
                </>
            ) : (
                <div className="text-center py-8">
                    <Typography variant="h6" color="textSecondary">
                        No resellers found
                    </Typography>
                </div>
            )}

            {/* Reseller Orders Dialog */}
            <Dialog
                open={detailsDialogOpen}
                onClose={handleCloseDetailsDialog}
                maxWidth="xl"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">
                            Orders for {selectedReseller?.name}
                            <Typography variant="body2" color="textSecondary">
                                Total Orders: {resellerSummary.total_orders || 0} | Total Sales: ${resellerSummary.total_sales || 0}
                            </Typography>
                        </Typography>
                        <IconButton onClick={handleCloseDetailsDialog}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent dividers>
                    {/* Filters for reseller orders */}
                    <div className="flex flex-col lg:flex-row gap-4 mb-4">
                        <FormControl size="small" className="w-full lg:w-48">
                            <InputLabel>Order Status</InputLabel>
                            <Select
                                value={orderStatusFilter}
                                label="Order Status"
                                onChange={(e) => setOrderStatusFilter(e.target.value)}
                            >
                                <MenuItem value="all">All</MenuItem>
                                <MenuItem value="pending">Pending</MenuItem>
                                <MenuItem value="processing">Processing</MenuItem>
                                <MenuItem value="complete">Complete</MenuItem>
                                <MenuItem value="cancel">Cancel</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl size="small" className="w-full lg:w-48">
                            <InputLabel>Payment Status</InputLabel>
                            <Select
                                value={paymentStatusFilter}
                                label="Payment Status"
                                onChange={(e) => setPaymentStatusFilter(e.target.value)}
                            >
                                <MenuItem value="all">All</MenuItem>
                                <MenuItem value="pending">Pending</MenuItem>
                                <MenuItem value="paid">Paid</MenuItem>
                                <MenuItem value="cancel">Cancel</MenuItem>
                            </Select>
                        </FormControl>

                        <Button
                            variant="contained"
                            color="success"
                            onClick={exportResellerOrdersToExcel}
                            disabled={exportLoading || resellerOrders.length === 0}
                            startIcon={<FaFileExcel />}
                            className="w-full lg:w-auto"
                        >
                            {exportLoading ? 'Exporting...' : 'Export Orders'}
                        </Button>
                    </div>

                    {/* Reseller Orders Table */}
                    {ordersLoading ? (
                        <LinearProgress />
                    ) : resellerOrders.length > 0 ? (
                        <>
                            <TableContainer component={Paper}>
                                <Table>
                                    <TableHead>
                                        <TableRow className="bg-primary">
                                            <TableCell align="center" className="!text-white">Order ID</TableCell>
                                            <TableCell align="center" className="!text-white">Service</TableCell>
                                            <TableCell align="center" className="!text-white">Service Type</TableCell>
                                            <TableCell align="center" className="!text-white">User Name</TableCell>
                                            <TableCell align="center" className="!text-white">Price</TableCell>
                                            <TableCell align="center" className="!text-white">Discount</TableCell>
                                            <TableCell align="center" className="!text-white">Net Price</TableCell>
                                            <TableCell align="center" className="!text-white">Created At</TableCell>
                                            <TableCell align="center" className="!text-white">Payment Status</TableCell>
                                            <TableCell align="center" className="!text-white">Order Status</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {resellerOrders.map((order) => (
                                            <TableRow key={order.request_id}>
                                                <TableCell align="center">{order.request_id}</TableCell>
                                                <TableCell align="center">{order.our_service || 'N/A'}</TableCell>
                                                <TableCell align="center">{order.our_service_type || 'N/A'}</TableCell>
                                                <TableCell align="center">{order.user_name || 'N/A'}</TableCell>
                                                <TableCell align="center">${order.price}</TableCell>
                                                <TableCell align="center">
                                                    {order.discount_type !== 'none' && (order.discount_for_auto_pay || order.discount_type === 'percentage')
                                                        ? `$${(order.discount * order.price / 100) + (order.discount_for_auto_pay * order.price / 100)}`
                                                        : `$${order.discount || 0}`}
                                                </TableCell>
                                                <TableCell align="center">
                                                    {order.discount_type !== 'none' && (order.discount_for_auto_pay || order.discount_type === 'percentage')
                                                        ? `$${order.price - (order.discount * order.price / 100) + (order.discount_for_auto_pay * order.price / 100)}`
                                                        : `$${order.price - (order.discount || 0)}`}
                                                </TableCell>
                                                <TableCell align="center">{order.created_at}</TableCell>
                                                <TableCell align="center">
                          <span
                              className={`badge w-24 text-darkTextColor ${
                                  order.status_payment === "paid"
                                      ? "bg-[#28a745]"
                                      : order.status_payment === "pending"
                                      ? "bg-[#f7cb73]"
                                      : "bg-[#ff6877]"
                              }`}
                          >
                            {order.status_payment}
                          </span>
                                                </TableCell>
                                                <TableCell align="center">
                          <span
                              className={`badge w-24 text-darkTextColor ${
                                  order.status === "processing"
                                      ? "bg-[#28a745]"
                                      : order.status === "pending"
                                      ? "bg-[#f7cb73]"
                                      : order.status === "complete"
                                          ? "bg-[#3f78e0]"
                                          : "bg-[#ff6877]"
                              }`}
                          >
                            {order.status}
                          </span>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <div className="mt-4 flex justify-center">
                                <TablePagination
                                    component="div"
                                    count={ordersTotal}
                                    page={ordersCurrentPage}
                                    onPageChange={handleOrdersPageChange}
                                    rowsPerPage={ordersRowsPerPage}
                                    onRowsPerPageChange={handleOrdersRowsPerPageChange}
                                    rowsPerPageOptions={[5, 10, 25, 50]}
                                />
                            </div>
                        </>
                    ) : (
                        <div className="text-center py-8">
                            <Typography variant="h6" color="textSecondary">
                                No orders found for this reseller
                            </Typography>
                        </div>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleCloseDetailsDialog}>Close</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default ResellersStatistics;